import React, { useState, useEffect, useRef } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, TextField, Box, DialogActions } from '@mui/material';

import { useTranslation } from "react-i18next";
import "../../css/components/Modals/CoverPickerModal.css";
import "../../css/main.css";

import FileUploadIcon from '@mui/icons-material/FileUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import ClearIcon from '@mui/icons-material/Clear';

const CoverPickerModal = ({ open, setCoverModalOpen, saveNewCoverImage, cover_image, cover_color }) => {
    const { t } = useTranslation();

    const [color, setColor] = useState(null);
    const [image, setImage] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resetCover, setResetCover] = useState(false);
    
    const fileInputRef = useRef(null);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (cover_image) {
            // Prepend backend URL if the image path is relative
            const fullImageURL = cover_image.startsWith('http') 
                ? cover_image 
                : `${cover_image}`;
            setImage(fullImageURL);
            setColor(null);
        } else {
            setColor(cover_color || null);
            setImage(null);
            setImageFile(null);
        }
        setLoading(false);

        return () => {
            if (image && image.startsWith('blob:')) {
                URL.revokeObjectURL(image);
            }
        };
    }, [cover_image, cover_color, open]);


    const handleColorChange = (event) => {
        setColor(event.target.value);
        setImage(null);
        setImageFile(null);
        setLoading(false);
    };

    const handleColorOpen = () => {
        setColor(color === null ? '#000000' : color);
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLoading(true);
            setImageFile(file);
            setColor(null);

            const imageURL = URL.createObjectURL(file);
            setImage(imageURL);
        }
    };


    const handleClearImage = () => {
        if (image && image.startsWith('blob:')) {
            URL.revokeObjectURL(image);
        }
        setImage(null);
        setImageFile(null);
        setColor(null);
        setLoading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setResetCover(true);
    };

    const handleClearColor = () => {
        setColor(null);
        setResetCover(true);
    };

    const handleClose = () => {
        setCoverModalOpen(false);
    };

    const handleSave = () => {
        // If no new image is uploaded and no color change, ensure we preserve the current state
        if (!imageFile && color === null && !resetCover) {
            handleClose();
            return; // No changes to save
        }
    
        // Pass only the new imageFile and color to saveNewCoverImage
        saveNewCoverImage(imageFile, color);
    
        handleClose();

        setResetCover(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} className="modal-background-color">
            <DialogTitle className="cover-picker-dialog-title">{t("modal.change_the_cover")}</DialogTitle>
            <DialogContent>
                <Box className="cover-picker-content-box">
                    {/* Color Picker */}
                    {!image && (
                        <TextField
                            type="color"
                            value={color}
                            onChange={handleColorChange}
                            onClick={handleColorOpen}
                            className="cover-picker-text"
                            InputProps={{ style: { color: color } }}
                        />
                    )}

                    {/* Image Upload Button */}
                    <Button
                        variant="contained"
                        component="label"
                        className="button-dark-bg"
                    >
                        <FileUploadIcon />
                        {t("modal.upload_image")}
                        <input
                            type="file"
                            id="cover-picker-image"
                            hidden
                            accept="image/*"
                            onChange={handleImageChange}
                            ref={fileInputRef}
                        />
                    </Button>

                    {/* Image Preview with Loading Fallback */}
                    {image && (
                        <Box className="cover-picker-img-box">
                            {loading && <TinyCustomFallback />}
                            {image && (
                                <img
                                    src={image}
                                    alt="Preview"
                                    className="cover-picker-img-dimensions"
                                    style={loading ? { display: 'none' } : {}}
                                    onLoad={() => {
                                        setLoading(false);
                                    }}
                                    onError={() => {
                                        console.error('Failed to load image');
                                        setLoading(false);
                                        setImage(null);
                                    }}
                                />
                            )}
                            <Button onClick={handleClearImage} className="button-dark-bg cover-picker-margin-top">
                                {t("modal.clear_image")}
                            </Button>
                        </Box>
                    )}

                    {/* Clear Color Button */}
                    {color !== null && !image && (
                        <Box className="cover-picker-img-box">
                            <Button onClick={handleClearColor} className="button-dark-bg cover-picker-margin-top">
                                <ClearIcon />
                                {t("modal.clear_color")}
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button className="decline-button" onClick={handleClose}>
                    <CancelIcon style={{ marginRight: 4 }} /> {t("global.cancel")}
                </Button>
                <Button className="accept-button" onClick={handleSave}>
                    <CheckCircleIcon style={{ marginRight: 4 }} />{t("global.save")}
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default CoverPickerModal;
